/**
 * Authentication Store
 * Zustand store for managing authentication state
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PortalUser } from '../../api/portal-user';
import { forgetAtSessionEnd } from '../../lib/session-end';

/**
 * Why a login failure is being reported. Stored as a code rather than a
 * message so the login screen can localize it — and so "we couldn't reach the
 * server" is never reported as "wrong password".
 */
export type AuthErrorCode = 'invalid_credentials' | 'network_error';

/**
 * A neutral, non-failure message to show on the login screen, set when the app
 * signs the user out on its own (currently only an expired session). Kept
 * separate from `error` because it is information, not something they did
 * wrong, and because it must survive the store reset that logging out performs.
 */
type AuthNoticeCode = 'session_expired';

/** The authentication state: the session, the user, and what the login screen says. */
interface AuthState {
  // Auth state
  isAuthenticated: boolean;
  user: PortalUser | null;
  token: string | null;
  isLoading: boolean;
  error: AuthErrorCode | null;
  notice: AuthNoticeCode | null;

  // Setters
  setUser: (user: PortalUser | null) => void;
  setToken: (token: string | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: AuthErrorCode | null) => void;
  setNotice: (notice: AuthNoticeCode | null) => void;

  // Reset state
  reset: () => void;
}

const initialState = {
  isAuthenticated: false,
  user: null,
  token: null,
  isLoading: false,
  error: null,
  notice: null,
};

/**
 * Authentication store — manages JWT session and current portal user
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      ...initialState,

      setUser: (user) => {
        set({ user, isAuthenticated: user !== null }, false, 'setUser');
      },

      setToken: (token) => {
        set({ token }, false, 'setToken');
      },

      setIsLoading: (isLoading) => {
        set({ isLoading }, false, 'setIsLoading');
      },

      setError: (error) => {
        set({ error }, false, 'setError');
      },

      setNotice: (notice) => {
        set({ notice }, false, 'setNotice');
      },

      reset: () => {
        // Preserve any pending notice: logging out is exactly when one is set
        // (an expired session), and clearing it here would discard the only
        // explanation the user gets for being returned to the login screen.
        set((state) => ({ ...initialState, notice: state.notice }), false, 'reset');
      },
    }),
    {
      name: 'auth-store',
    },
  ),
);

// A session's end resets the sign-in, keeping the notice that says why the user was signed out.
forgetAtSessionEnd(() => useAuthStore.getState().reset());
